/**
 * App root — spec §F1/§F3.
 * Providers: TanStack Query (one client, staleTime 5 min — §F1) and react-router.
 * Layout: the §F3 topbar plus `#views` (absolute inset below it) holding the routed view,
 * and the single `#toast`
 * and `.charttip` nodes (§F2).
 * `SplashLanding` is the §F4 overlay: a sibling of the routes rather than one of them, so
 * the live app stays mounted and visible behind its backdrop. `SignInGate` (client review
 * R1) is the same shape one step earlier — gate → splash → app, each once per session.
 * `SessionSync` renders nothing: it is the §D1 `GET /auth/me` boot call that gives the
 * store its `role` and `user` (see lib/session.ts). `PermalinkSync` renders nothing either:
 * it settles the ids a §F1 share-hash restored against `/indicators` and `/layers`.
 *
 * `ErrorBoundary` wraps the routed view and the three overlays. React unmounts the entire
 * tree when a render throws and nothing catches it, so without one the reader gets a white
 * page with no error and no toast; with one they get a sentence and a reload button. It sits
 * *inside* the providers, so the query client and the router survive the failure, and
 * outside the routes, so it covers every view rather than one.
 */
import { useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { createQueryClient } from '../lib/api';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Toast } from '../components/Toast';
import { ChartTip } from '../components/charts/ChartTip';
import { SplashLanding } from '../features/landing';
import { SignInGate } from '../features/landing/SignInGate';
import { PermalinkSync } from './PermalinkSync';
import { SessionSync } from './SessionSync';
import { Topbar } from './Topbar';
import { AppRoutes } from './routes';

export function App() {
  // useState so the client survives re-renders but is per-mount (tests get a fresh one).
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionSync />
      {/* §F1 permalink: `main.tsx` has already applied the hash; this confirms the ids it
          carried against `/indicators` and `/layers` (see PermalinkSync). */}
      <PermalinkSync />
      <BrowserRouter>
        <ErrorBoundary>
          <Topbar />
          <AppRoutes />
          {/* §F4: an overlay, not a route — the spec requires the live app visible behind
              it, and a route would unmount the map the reader is about to be flown to. It is
              a sibling of the routes for that reason, and it renders nothing when closed. */}
          <SplashLanding />
          {/* Client review R1: the session's first page, drawn over the splash and closed
              before it. Last of the three overlays so it is last in paint order too — the
              gate must cover the landing card, not the other way round. */}
          <SignInGate />
        </ErrorBoundary>
        {/* Outside the boundary: §F2's toast and tooltip are single nodes the rest of the
            app writes into, and they are exactly what a caught failure wants to still be
            able to reach. */}
        <Toast />
        {/* §F2: one tooltip node for every chart in the app (see components/charts). */}
        <ChartTip />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
